import { getBackendOptions, MultiBackend, NodeModel, Tree } from '@minoru/react-dnd-treeview';
import logo from 'assets/logo-name.svg';
import { FC, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { DragPreview } from './DragPreview';
import { Placeholder } from './Placeholder';
import { LogoH1, LogoSmall, SidebarLogoDiv, StyledSider } from './Sidebar.styles';
import { SidebarNode, SidebarNodeType } from './SidebarNode';
import styles from './Sidebar.module.css';
import { Button, Collapse, Dropdown, Menu } from 'antd';
import { DoubleRightOutlined, PlusOutlined, StarOutlined, StarFilled } from '@ant-design/icons';
import { getFavouritesNavigation, saveFavouritesNavigation } from 'store/navigation/client';
import { defaultMain, declarationsMenu, operationsMenu, items } from './sidebarData';
import { MenuItemType } from 'antd/lib/menu/hooks/useItems';
import { Link } from 'react-router-dom';

interface Props {
    collapsed: boolean;
}

type CustomMenuItemType = MenuItemType & {
    data?: {
        url: string;
        activeOn: string;
        customTrigger: string;
    };
};

const Sidebar: FC<Props> = ({ collapsed }) => {
    const [favourites, setFavourites] = useState<SidebarNodeType[]>([]);
    const [isInitial, setIsInitial] = useState<boolean>(true);

    useEffect(() => {
        const fetchNavigation = async () => {
            const favouritesNavigation = await getFavouritesNavigation();
            if (favouritesNavigation?.favorites) {
                setFavourites(favouritesNavigation.favorites);
                setIsInitial(false);
            } else {
                setIsInitial(true);
            }
        };
        fetchNavigation();
    }, []);

    const handleFavouriteDrop = async (newTree: SidebarNodeType[]) => {
        try {
            const updatedFavourites = await saveFavouritesNavigation(newTree, isInitial);
            setFavourites(updatedFavourites);

            if (isInitial) setIsInitial(false);
        } catch (error) {
            console.error('Error updating favourites:', error);
        }
    };

    const handleFavourite = (node: SidebarNodeType) => {
        let newFavourites;
        if (favourites.some((fav) => fav.id === node.id)) {
            newFavourites = favourites.filter((fav) => fav.id !== node.id);
        } else {
            newFavourites = [
                ...favourites,
                { ...node, id: node.id, parent: 0, data: { ...node.data, favourite: true } },
            ];
        }
        handleFavouriteDrop(newFavourites);
    };

    const transformToSidebarNode = (item: CustomMenuItemType): SidebarNodeType => ({
        id: item.key,
        text: typeof item.label === 'string' ? item.label : '',
        parent: 0,
        droppable: false,
        data: item.data ?? { url: '', activeOn: '', customTrigger: '' },
    });

    const menuItems = (
        <Menu style={{ borderRadius: '8px' }}>
            {items?.map((group) =>
                group && 'children' in group ? (
                    <Menu.ItemGroup className={styles.menu} key={group.key} title={group.label}>
                        {group.children?.map((child) =>
                            child && 'key' in child && 'label' in child ? (
                                <Menu.Item
                                    key={child?.key}
                                    onClick={(e: any) => {
                                        handleFavourite(transformToSidebarNode(child as MenuItemType));
                                        e.stopPropagation();
                                    }}
                                    className={styles.menuItem}
                                >
                                    <span
                                        className={`${styles.menuSpan} ${
                                            favourites.some((fav) => fav.id === child?.key) ? 'favourite' : ''
                                        }`}
                                    >
                                        {(child as MenuItemType).label}
                                        <span className={styles.iconStar}>
                                            {favourites.some((fav) => fav.id === child?.key) ? (
                                                <StarFilled style={{ color: '#3430b4' }} />
                                            ) : (
                                                <StarOutlined style={{ color: 'gray' }} />
                                            )}
                                        </span>
                                    </span>
                                </Menu.Item>
                            ) : null
                        )}
                    </Menu.ItemGroup>
                ) : null
            )}
        </Menu>
    );

    const [activeKeys, setActiveKeys] = useState<string[]>(['declarations', 'favourites']);

    return (
        <StyledSider
            collapsed={collapsed}
            width="265"
            style={{ overflowX: collapsed ? 'hidden' : undefined, display: 'flex', flexDirection: 'column' }}
        >
            <SidebarLogoDiv>
                {collapsed ? (
                    <LogoSmall>
                        <svg
                            viewBox="0 0 24 26"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ height: '26px', width: '24px' }}
                        >
                            <path
                                d="M23.9513 25.1089H14.4263V21.1979H17.6605L11.9997 9.72844L6.33939 21.1979H9.57309V25.1089H0.048584L11.9997 0.891357L23.9513 25.1089Z"
                                fill="#3430b4"
                            />
                        </svg>
                    </LogoSmall>
                ) : (
                    <LogoH1>
                        <img src={logo} alt="ddispatch" style={{ width: '156px', marginTop: 7 }} />
                    </LogoH1>
                )}
            </SidebarLogoDiv>
            <DndProvider backend={MultiBackend} options={getBackendOptions()}>
                <Tree
                    rootId={0}
                    tree={defaultMain}
                    onDrop={() => {}}
                    render={(node: NodeModel<any>, { depth, isOpen, onToggle, handleRef }) => (
                        <SidebarNode
                            depth={depth}
                            isOpen={isOpen}
                            node={node}
                            onToggle={onToggle}
                            handleRef={handleRef}
                            collapsed={collapsed}
                        />
                    )}
                    canDrop={() => {
                        return false;
                    }}
                    canDrag={() => {
                        return false;
                    }}
                    classes={{
                        root: styles.treeRoot,
                        draggingSource: styles.draggingSource,
                        placeholder: styles.placeholderContainer,
                        listItem: styles.listItem,
                    }}
                />
            </DndProvider>

            <Collapse
                defaultActiveKey={'favourites'}
                activeKey={collapsed ? ['favourites'] : activeKeys}
                ghost
                className={`${styles.collapse} ${collapsed ? styles.collapsed : ''}`}
                bordered={true}
                expandIconPosition="end"
                collapsible={collapsed ? 'disabled' : undefined}
                onChange={(keys) => setActiveKeys(keys as string[])}
            >
                <Collapse.Panel
                    header={
                        <div>
                            {!collapsed && (
                                <div
                                    style={{
                                        display: 'inline-flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '175px',
                                    }}
                                >
                                    <span>Favourites</span>
                                    <Dropdown overlay={menuItems} trigger={['click']}>
                                        <Button
                                            style={{
                                                display: collapsed ? 'none' : 'inline-flex',
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            type="link"
                                            icon={<PlusOutlined />}
                                        />
                                    </Dropdown>
                                </div>
                            )}
                        </div>
                    }
                    key="favourites"
                >
                    {favourites.length > 0 && !collapsed ? (
                        <DndProvider backend={MultiBackend} options={getBackendOptions()}>
                            <Tree
                                rootId={0}
                                tree={collapsed ? favourites.slice(1) : favourites}
                                insertDroppableFirst={false}
                                sort={false}
                                onDrop={handleFavouriteDrop}
                                dragPreviewRender={(monitorProps) => <DragPreview monitorProps={monitorProps as any} />}
                                render={(node: NodeModel<any>, { depth, isOpen, onToggle, handleRef }) => (
                                    <SidebarNode
                                        depth={depth}
                                        isOpen={isOpen}
                                        node={node}
                                        onToggle={onToggle}
                                        handleRef={handleRef}
                                        favourites={favourites}
                                        actions={
                                            collapsed
                                                ? undefined
                                                : [
                                                      {
                                                          text: favourites.find((n) => n.id === node.id)
                                                              ? 'Remove from Favourites'
                                                              : 'Add to Favourites',
                                                          action: 'favourite',
                                                          onAction: handleFavourite,
                                                          hidden: node.droppable,
                                                      },
                                                  ]
                                        }
                                        collapsed={collapsed}
                                    />
                                )}
                                canDrop={(_, { dragSource, dropTargetId }) => {
                                    const isFavourite = (dragSource as SidebarNodeType | undefined)?.data?.favourite;
                                    if (!isFavourite) return false;
                                    if (dragSource?.parent === dropTargetId) {
                                        return true;
                                    }
                                }}
                                dropTargetOffset={10}
                                classes={{
                                    root: styles.treeRoot,
                                    draggingSource: styles.draggingSource,
                                    placeholder: styles.placeholderContainer,
                                    listItem: styles.listItem,
                                }}
                                placeholderRender={(node, { depth }) => <Placeholder node={node} depth={depth} />}
                            />
                        </DndProvider>
                    ) : (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                marginTop: '12px',
                            }}
                        >
                            {collapsed && (
                                <Dropdown
                                    trigger={['click']}
                                    overlay={
                                        <Menu style={{ borderRadius: '8px' }}>
                                            {favourites.map((favourite) => (
                                                <Menu.Item title="" className={styles.menuItem} key={favourite.id}>
                                                    <Link
                                                        to={favourite.data?.url || ''}
                                                        style={{ background: 'white' }}
                                                        replace
                                                    >
                                                        <span className={styles.menuSpan}>{favourite.text}</span>
                                                    </Link>
                                                </Menu.Item>
                                            ))}
                                        </Menu>
                                    }
                                >
                                    <StarOutlined />
                                </Dropdown>
                            )}
                        </div>
                    )}
                </Collapse.Panel>
            </Collapse>
            <Collapse
                defaultActiveKey={'declarations'}
                activeKey={collapsed ? ['declarations'] : activeKeys}
                ghost
                expandIcon={({ isActive }) => <DoubleRightOutlined rotate={isActive ? 90 : 0} />}
                bordered={true}
                expandIconPosition="end"
                className={`${styles.collapse} ${collapsed ? styles.collapsed : ''}`}
                collapsible={collapsed ? 'disabled' : undefined}
                onChange={(keys) => setActiveKeys(keys as string[])}
            >
                <Collapse.Panel header={!collapsed && 'Declarations'} key="declarations">
                    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
                        <Tree
                            rootId={1}
                            tree={
                                collapsed
                                    ? declarationsMenu.filter((node) => [8, 9, 10, 11].includes(Number(node.id)))
                                    : declarationsMenu
                            }
                            initialOpen={false}
                            sort={false}
                            enableAnimateExpand
                            onDrop={() => {}}
                            render={(node: NodeModel<any>, { depth, isOpen, onToggle, handleRef }) => (
                                <SidebarNode
                                    depth={depth}
                                    isOpen={isOpen}
                                    node={node}
                                    onToggle={onToggle}
                                    handleRef={handleRef}
                                    actions={
                                        collapsed
                                            ? undefined
                                            : [
                                                  {
                                                      text: favourites.find((n) => n.id === node.id)
                                                          ? 'Remove from Favourites'
                                                          : 'Add to Favourites',
                                                      action: 'favourite',
                                                      onAction: handleFavourite,
                                                      hidden: node.droppable,
                                                  },
                                              ]
                                    }
                                    collapsed={collapsed && declarationsMenu}
                                />
                            )}
                            canDrop={() => {
                                return false;
                            }}
                            canDrag={() => {
                                return false;
                            }}
                            classes={{
                                root: styles.treeRoot,
                                draggingSource: styles.draggingSource,
                                placeholder: styles.placeholderContainer,
                                listItem: styles.listItem,
                            }}
                        />
                    </DndProvider>
                </Collapse.Panel>
            </Collapse>
            <Collapse
                activeKey={'operations'}
                ghost
                className={`${styles.collapse} ${styles.operations}`}
                bordered={true}
                collapsible={collapsed ? 'disabled' : undefined}
            >
                <Collapse.Panel header={!collapsed && 'Operations'} key="operations" showArrow={false}>
                    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
                        <Tree
                            rootId={2}
                            tree={operationsMenu}
                            sort={false}
                            enableAnimateExpand={false}
                            onDrop={() => {}}
                            render={(node: NodeModel<any>, { depth, isOpen, onToggle, handleRef }) => (
                                <SidebarNode
                                    depth={depth}
                                    isOpen={isOpen}
                                    node={node}
                                    onToggle={onToggle}
                                    handleRef={handleRef}
                                    collapsed={collapsed}
                                />
                            )}
                            canDrop={() => {
                                return false;
                            }}
                            canDrag={() => {
                                return false;
                            }}
                            classes={{
                                root: styles.treeRoot,
                                draggingSource: styles.draggingSource,
                                placeholder: styles.placeholderContainer,
                                listItem: styles.listItem,
                            }}
                        />
                    </DndProvider>
                </Collapse.Panel>
            </Collapse>
        </StyledSider>
    );
};
export default Sidebar;
